/**
 * Package & Feature Limit Types
 *
 * Types for package/plan features and usage limits
 */

export type PackageSource = 'DIRECT' | 'APPSUMO' | 'STRIPE' | 'TRIAL' | 'MANUAL';
export type FeatureType = 'BOOLEAN' | 'INTEGER' | 'STRING';

/**
 * Package Feature
 */
export interface PackageFeature {
  featureName: string;
  featureType: FeatureType;
  featureValue: string;
}

/**
 * Package/Plan
 */
export interface Package {
  id: number;
  name: string;
  source: PackageSource;
  isAgency: boolean;
  maxChannels: number | null;  // null = unlimited
  maxUsers: number | null;     // null = unlimited
  maxAITokens: number;         // Always has a value
  maxStorage: number;          // Always has a value
  maxCalendars: number | null; // null = unlimited
  maxVariations: number | null; // null = unlimited
  maxBrandProfiles?: number | null; // Brand World profiles per brand, null = unlimited
  maxWorkspaces: number | null;
  whitelabelEnabled: boolean;
  canvaEnabled: boolean;
  gdriveEnabled: boolean;
  brandCheckEnabled: boolean;
  analyticsEnabled: boolean;
  features: PackageFeature[];
}

/**
 * Brand Space allowance (Brand Portfolio). Only ACTIVE brands use a Brand Space.
 * `total` and `free` are null when the Plan is unlimited.
 */
export interface BrandSpaceUsage {
  included: number | null;
  extra: number;
  total: number | null;
  used: number;
  paused: number;
  hidden: number;
  free: number | null;
}

/** A brand's view of its Brand Portfolio's shared storage pool (MB). Present only for a brand inside a portfolio. */
export interface BrandStoragePool {
  /** The brand's own included storage (plan + own rows), null = unlimited. */
  includedMb: number | null;
  /** Pool size: the portfolio's live storage add-ons. */
  poolMb: number;
  /** Pool used by ALL brands of the portfolio. */
  poolUsedMb: number;
  /** What THIS brand takes from the pool right now. */
  brandPoolUsedMb: number;
}

/** Portfolio-level pool view for team-less staff: size, used, which brands overflow into it. */
export interface PortfolioStoragePool {
  poolMb: number;
  poolUsedMb: number;
  brands: Array<{ teamId: number; name: string; isActive: boolean; usedMb: number; includedMb: number | null; overflowMb: number }>;
}

/**
 * Package Usage
 */
export interface PackageUsage {
  /** Brand inside a Brand Portfolio: its slice of the shared storage pool. */
  storagePool?: BrandStoragePool;
  /** Brand Portfolio (team-less staff): the shared storage pool. */
  portfolioStoragePool?: PortfolioStoragePool;
  /** Brand Portfolio only: Brand Space allowance. total/free null = unlimited */
  brandSpaces?: BrandSpaceUsage;
  channels: number;
  users: number;
  calendars: number;
  storageMb: number;
  aiTokensUsed: number;
  aiTokensAddonRemaining: number;
  /** Server truth: max(0, plan allowance - used) + active pack tokens. Optional for older backends. */
  aiTokensRemaining?: number;
  posts: number;
}

/**
 * Entitlement feature keys (mirror of backend/src/constants/package-features.ts ENTITLEMENT_FEATURES).
 */
export type EntitlementFeatureKey =
  | 'analytics_hub'
  | 'analytics_compare'
  | 'best_times'
  | 'lara_insights'
  | 'success_metrics'
  | 'analytics'
  | 'trend_suggestions'
  | 'brand_world'
  | 'auto_translate'
  | 'brand_check'
  | 'weekly_goals'
  | 'festivities'
  | 'team_roles'
  | 'wordpress'
  | 'gdrive'
  | 'canva'
  | 'whitelabel';

/** LIFETIME_GATING_MODE as exposed by GET /api/teams/package */
export type GatingMode = 'off' | 'shadow' | 'enforce';

/**
 * Resolved entitlements + gating mode (GET /api/teams/package). `null` when the resolver failed:
 * the UI then locks nothing. `upsellPlanSlugs` is optional (older backends omit it).
 */
export interface EntitlementsPayload {
  features: Partial<Record<EntitlementFeatureKey, boolean>>;
  limits: Record<string, number | null>;
  mode: GatingMode;
  upsellPlanSlugs?: Partial<Record<EntitlementFeatureKey, string | null>>;
  /** Monthly quota limits per quota key (null = unlimited). Older backends omit it. */
  quotas?: Record<string, number | null>;
  /** Platform restriction of the package. Older backends omit it (= all platforms). */
  platforms?: PlatformRestriction;
}

/** The 9 social platforms a package can allow (CANVA / GDRIVE / WORDPRESS stay on their feature gates). */
export type GatedPlatform =
  | 'LINKEDIN'
  | 'FACEBOOK'
  | 'INSTAGRAM'
  | 'YOUTUBE'
  | 'TIKTOK'
  | 'PINTEREST'
  | 'THREADS'
  | 'GOOGLE_MY_BUSINESS'
  | 'BLUESKY';

export const GATED_PLATFORMS: readonly GatedPlatform[] = [
  'LINKEDIN',
  'FACEBOOK',
  'INSTAGRAM',
  'YOUTUBE',
  'TIKTOK',
  'PINTEREST',
  'THREADS',
  'GOOGLE_MY_BUSINESS',
  'BLUESKY',
];

export interface PlatformRestriction {
  restricted: boolean;
  allowed: string[];
  /** Cheapest plan slug per platform (computed per platform by the backend). Older backends omit it. */
  upsellPlanSlugs?: Partial<Record<string, string | null>>;
}

/** One quota in GET /api/teams/quota-usage */
export interface QuotaUsageEntry {
  /** null = not counted (backend mode 'off'); readers must not render it as a number */
  used: number | null;
  /** null = unlimited */
  limit: number | null;
  /** ISO timestamp of the next reset (start of next UTC month) */
  resetsAt: string;
}

/** GET /api/teams/quota-usage (data) */
export interface QuotaUsageResponse {
  quotas: Record<string, QuotaUsageEntry>;
  platforms: PlatformRestriction;
  mode: GatingMode;
}

/**
 * Team Package Response
 */
export interface TeamPackageResponse {
  package: Package;
  usage: PackageUsage;
  entitlements?: EntitlementsPayload | null;
  expiresAt: string | null;
  isExpired: boolean;
}
